import type { ErrorBody, LoginResponse, Me } from '@my-eng/shared'
import { getInitDataRaw } from './telegram.js'

const BASE_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:8787'

export class ApiError extends Error {
  readonly code: string
  readonly status: number

  constructor(code: string, message: string, status: number) {
    super(message)
    this.name = 'ApiError'
    this.code = code
    this.status = status
  }
}

/**
 * The token lives in memory only. It is short-lived and re-mintable from
 * `initData` on every app open, so persisting it would add a theft surface and
 * buy nothing.
 */
let token: string | null = null

export function getToken(): string | null {
  return token
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers)
  headers.set('Content-Type', 'application/json')
  if (token) headers.set('Authorization', `Bearer ${token}`)

  const response = await fetch(`${BASE_URL}${path}`, { ...init, headers })

  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as ErrorBody | null
    throw new ApiError(
      body?.error.code ?? 'INTERNAL',
      body?.error.message ?? `Request failed with ${response.status}`,
      response.status,
    )
  }

  return response.json() as Promise<T>
}

export async function login(): Promise<LoginResponse> {
  const initData = getInitDataRaw()
  if (!initData) {
    throw new ApiError('UNAUTHORIZED', 'Открой приложение через Telegram', 401)
  }

  const result = await request<LoginResponse>('/api/v1/auth/login', {
    method: 'POST',
    body: JSON.stringify({ initData }),
  })

  token = result.token
  return result
}

export function getMe(): Promise<Me> {
  return request<Me>('/api/v1/auth/me')
}
